import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

const { FiCheckCircle, FiX, FiShare2, FiHome, FiEdit3 } = FiIcons

const SuccessNotification = ({ 
  isVisible, 
  onClose, 
  title, 
  message, 
  actions = [], 
  autoCloseDelay = 5000,
  type = 'success' // 'success', 'info', 'warning', 'error'
}) => {
  React.useEffect(() => {
    if (isVisible && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose?.()
      }, autoCloseDelay)
      
      return () => clearTimeout(timer)
    }
  }, [isVisible, autoCloseDelay, onClose])

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bgGradient: 'from-green-50 to-emerald-50',
          borderColor: 'border-green-200',
          iconColor: 'text-green-600',
          titleColor: 'text-green-800',
          messageColor: 'text-green-700',
          icon: FiCheckCircle
        }
      case 'info':
        return {
          bgGradient: 'from-blue-50 to-cyan-50',
          borderColor: 'border-blue-200',
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-800',
          messageColor: 'text-blue-700',
          icon: FiCheckCircle
        }
      case 'warning':
        return {
          bgGradient: 'from-yellow-50 to-amber-50',
          borderColor: 'border-yellow-200',
          iconColor: 'text-yellow-600',
          titleColor: 'text-yellow-800',
          messageColor: 'text-yellow-700',
          icon: FiCheckCircle
        }
      case 'error':
        return {
          bgGradient: 'from-red-50 to-rose-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-600',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          icon: FiX
        }
      default:
        return {
          bgGradient: 'from-green-50 to-emerald-50',
          borderColor: 'border-green-200',
          iconColor: 'text-green-600',
          titleColor: 'text-green-800',
          messageColor: 'text-green-700',
          icon: FiCheckCircle
        }
    }
  }

  const typeStyles = getTypeStyles()

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            {/* 通知卡片 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 25,
                duration: 0.3 
              }}
              className={`relative max-w-md w-full bg-gradient-to-br ${typeStyles.bgGradient} 
                         border ${typeStyles.borderColor} rounded-2xl shadow-2xl overflow-hidden`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 裝飾性頂部條 */}
              <div className={`h-1 bg-gradient-to-r ${
                type === 'success' ? 'from-green-400 to-emerald-500' :
                type === 'info' ? 'from-blue-400 to-cyan-500' :
                type === 'warning' ? 'from-yellow-400 to-amber-500' :
                'from-red-400 to-rose-500'
              }`} />
              
              {/* 關閉按鈕 */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-white hover:bg-opacity-50 
                          transition-colors duration-200 group"
              >
                <SafeIcon 
                  icon={FiX} 
                  className="h-5 w-5 text-gray-500 group-hover:text-gray-700" 
                />
              </button>

              <div className="p-6">
                {/* 主要內容 */}
                <div className="flex items-start space-x-4">
                  {/* 圖標 */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 400 }}
                    className={`flex-shrink-0 w-12 h-12 rounded-full bg-white bg-opacity-80 
                               flex items-center justify-center shadow-lg`}
                  >
                    <SafeIcon 
                      icon={typeStyles.icon} 
                      className={`h-6 w-6 ${typeStyles.iconColor}`} 
                    />
                  </motion.div>

                  {/* 文字內容 */}
                  <div className="flex-1 min-w-0">
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                      className={`text-lg font-semibold ${typeStyles.titleColor} mb-2 leading-tight`}
                    >
                      {title}
                    </motion.h3>
                    
                    {message && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className={`text-sm ${typeStyles.messageColor} leading-relaxed`}
                      >
                        {message}
                      </motion.p>
                    )}
                  </div>
                </div>

                {/* 操作按鈕 */}
                {actions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="mt-6 flex flex-col sm:flex-row gap-3"
                  >
                    {actions.map((action, index) => (
                      <motion.button
                        key={action.key || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.05 }}
                        onClick={action.onClick}
                        className={`flex items-center justify-center space-x-2 px-4 py-2.5 
                                   rounded-xl font-medium transition-all duration-200 transform 
                                   hover:scale-105 active:scale-95 shadow-sm
                                   ${action.variant === 'primary' 
                                     ? `bg-gradient-to-r ${
                                         type === 'success' ? 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700' :
                                         type === 'info' ? 'from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700' :
                                         type === 'warning' ? 'from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700' :
                                         'from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700'
                                       } text-white shadow-md` 
                                     : 'bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-700 hover:text-gray-900 border border-gray-200'
                                   }`}
                      >
                        {action.icon && (
                          <SafeIcon icon={action.icon} className="h-4 w-4" />
                        )}
                        <span className="text-sm">{action.label}</span>
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                {/* 自動關閉進度條 */}
                {autoCloseDelay > 0 && (
                  <motion.div
                    className="mt-4 h-1 bg-white bg-opacity-30 rounded-full overflow-hidden"
                  >
                    <motion.div
                      initial={{ width: "100%" }}
                      animate={{ width: "0%" }}
                      transition={{ duration: autoCloseDelay / 1000, ease: "linear" }}
                      className={`h-full bg-gradient-to-r ${
                        type === 'success' ? 'from-green-400 to-emerald-500' :
                        type === 'info' ? 'from-blue-400 to-cyan-500' :
                        type === 'warning' ? 'from-yellow-400 to-amber-500' :
                        'from-red-400 to-rose-500'
                      }`}
                    />
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default SuccessNotification